import React, { useState, useMemo } from 'react';
import { useMailProvider } from './MailProviderContext';
import type { MailMessage } from '../../lib/stores/mail/types';
import { useMailOpenStatus, type MailOpenInfo } from '../../lib/hooks/useMailOpenStatus';
import { MailTaskModal } from './MailTaskModal';
import { MailLinkEntityDrawer } from './MailLinkEntityDrawer';
import { ThreadLinkBar } from './ThreadLinkBar';
import { SanitizedHtml } from './SanitizedHtml';
import { useApp } from '../AppProvider';
import { pickThreadClient, findThreadSenderEmail, isInternalClient } from '../../lib/mail/threadLink';
import { useInternalEmails } from '../../lib/mail/useInternalEmails';
import {
  ArrowLeft,
  Reply,
  ReplyAll,
  Forward,
  Archive,
  Trash2,
  Download,
  Loader,
  User,
  Paperclip,
  ChevronDown,
  ChevronUp,
  MailOpen,
  Mail,
  ListTodo,
  UserPlus,
  Link2,
  FolderInput,
  Folder,
} from 'lucide-react';

interface MailThreadViewProps {
  organizationId: string;
  userId: string;
}

export function MailThreadView({ organizationId, userId }: MailThreadViewProps) {
  const { useStore, provider } = useMailProvider();
  const {
    selectedMessages,
    messagesLoading,
    selectedThreadId,
    viewMode,
    deselectThread,
    markOpenThreadUnread,
    setComposing,
    setReplyingTo,
    archiveThread,
    trashThread,
    moveThreadToFolder,
    linkThreadToClient,
    labels,
  } = useStore();

  const { toast } = useApp();
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showLinkDrawer, setShowLinkDrawer] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [trashing, setTrashing] = useState(false);
  const [markingUnread, setMarkingUnread] = useState(false);
  const [showMoveMenu, setShowMoveMenu] = useState(false);
  const [moving, setMoving] = useState(false);

  // Il drawer usa questo array come chiave di inizializzazione: ricrearlo a ogni
  // render gli farebbe reimpostare il form mentre l'utente lo sta compilando.
  const linkThreads = useMemo(
    () => [{ threadId: selectedThreadId || '', messages: selectedMessages }],
    [selectedThreadId, selectedMessages],
  );

  // "Sposta in cartella": solo Outlook e solo se esistono cartelle custom (type 'folder').
  // Gmail non produce mai label 'folder' → menu mai mostrato (zero impatto).
  const folderLabels = useMemo(
    () => Object.values(labels).filter(l => l.type === 'folder'),
    [labels]
  );
  const canMoveToFolder = provider === 'microsoft' && folderLabels.length > 0;

  const handleMove = async (folderLabelId: string) => {
    if (!selectedThreadId) return;
    setShowMoveMenu(false);
    setMoving(true);
    // folderLabelId è namespaced 'FOLDER:<graphId>' → passo l'id Graph grezzo allo store.
    const folderId = folderLabelId.startsWith('FOLDER:') ? folderLabelId.slice('FOLDER:'.length) : folderLabelId;
    const ok = await moveThreadToFolder(organizationId, userId, selectedThreadId, folderId);
    setMoving(false);
    toast[ok ? 'success' : 'error'](ok ? 'Spostato nella cartella' : 'Errore spostamento');
  };

  // Lead/cliente collegato al thread. Non si prende la prima associazione trovata:
  // in un thread misto e` quasi sempre quella del messaggio inviato dall'utente,
  // cioe` la scheda dell'utente stesso. La regola sta in `pickThreadClient`.
  const internalEmails = useInternalEmails(organizationId);
  const linkedClient = useMemo(
    () => pickThreadClient(selectedMessages, internalEmails),
    [selectedMessages, internalEmails],
  );
  const threadSenderEmail = useMemo(
    () => findThreadSenderEmail(selectedMessages, internalEmails),
    [selectedMessages, internalEmails],
  );

  const handleUnlink = async () => {
    if (!selectedThreadId) return;
    await linkThreadToClient(organizationId, userId, selectedThreadId, null);
    toast.success('Collegamento rimosso');
  };

  // Stato apertura (tracking) dei messaggi inviati del thread, chiave = message_id.
  const openStatus = useMailOpenStatus(selectedMessages.map(m => m.message_id));

  // Bozza esistente per questo thread (mostrata come ultimo messaggio)
  const draft = useStore(s => (selectedThreadId ? s.drafts[selectedThreadId] : undefined));
  const openDraftEditor = useStore(s => s.openDraftEditor);

  if (messagesLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader className="h-6 w-6 animate-spin text-[#787774]" />
      </div>
    );
  }

  if (selectedMessages.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-[#787774]">
        Thread non trovato
      </div>
    );
  }

  const subject = selectedMessages[0]?.subject || '(Nessun oggetto)';

  const handleReply = (msg: MailMessage, mode: 'reply' | 'replyAll' | 'forward' = 'reply') => {
    setReplyingTo(msg.message_id, msg.thread_id, mode);
    setComposing(true);
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-950">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-800">
        <button
          onClick={deselectThread}
          className={`p-1 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 text-[#787774] ${viewMode === 'list' ? '' : 'lg:hidden'}`}
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <h2 className="text-sm font-semibold text-[#37352F] dark:text-white truncate flex-1">
          {subject}
        </h2>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={async () => {
              setMarkingUnread(true);
              const ok = await markOpenThreadUnread(organizationId, userId);
              setMarkingUnread(false);
              if (!ok) toast.error('Non sono riuscito a segnarla come non letta');
            }}
            disabled={markingUnread}
            className="p-1.5 text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 rounded-md transition-colors"
            title="Segna come non letto"
            aria-label="Segna come non letto"
          >
            {markingUnread ? <Loader className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
          </button>
          <button
            onClick={async () => {
              if (!selectedThreadId) return;
              setArchiving(true);
              const ok = await archiveThread(organizationId, userId, selectedThreadId);
              setArchiving(false);
              toast[ok ? 'success' : 'error'](ok ? 'Archiviato' : 'Errore archiviazione');
            }}
            disabled={archiving}
            className="p-1.5 text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 rounded-md transition-colors"
            title="Archivia"
          >
            {archiving ? <Loader className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
          </button>
          <button
            onClick={async () => {
              if (!selectedThreadId) return;
              setTrashing(true);
              const ok = await trashThread(organizationId, userId, selectedThreadId);
              setTrashing(false);
              toast[ok ? 'success' : 'error'](ok ? 'Spostato nel cestino' : 'Errore');
            }}
            disabled={trashing}
            className="p-1.5 text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 rounded-md transition-colors"
            title="Cestina"
          >
            {trashing ? <Loader className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
          {canMoveToFolder && (
            <div className="relative">
              <button
                onClick={() => setShowMoveMenu(v => !v)}
                disabled={moving}
                className="p-1.5 text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 rounded-md transition-colors"
                title="Sposta in cartella"
              >
                {moving ? <Loader className="h-4 w-4 animate-spin" /> : <FolderInput className="h-4 w-4" />}
              </button>
              {showMoveMenu && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setShowMoveMenu(false)} />
                  <div className="absolute top-full right-0 mt-1 w-56 max-h-72 overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.12)] dark:border-gray-700 py-1 z-30">
                    <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#787774] dark:text-white/40">Sposta in</div>
                    {folderLabels.map(l => (
                      <button
                        key={l.id}
                        onClick={() => handleMove(l.id)}
                        className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5"
                      >
                        <Folder className="h-3.5 w-3.5 flex-shrink-0 text-[#787774]" />
                        <span className="truncate">{l.name}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <button
            onClick={() => setShowLinkDrawer(true)}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors ${
              linkedClient
                ? 'text-[#2383E2] bg-[#2383E2]/10 hover:bg-[#2383E2]/15'
                : 'text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5'
            }`}
            title={linkedClient ? 'Gestisci collegamento' : 'Collega questo thread a cliente, offerta o progetto'}
          >
            {linkedClient ? <Link2 className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
            <span className="max-w-[120px] truncate">
              {linkedClient
                ? (linkedClient.business_name || linkedClient.contact_name || 'Collegato')
                : 'Collega'}
            </span>
          </button>
          <button
            onClick={() => setShowTaskModal(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs text-[#787774] dark:text-white/60 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 rounded-md transition-colors"
            title="Crea task da questo thread"
          >
            <ListTodo className="h-3.5 w-3.5" />
            Crea task
          </button>
          <span className="text-xs text-[#787774] dark:text-white/40 ml-1">
            {selectedMessages.length} {selectedMessages.length === 1 ? 'messaggio' : 'messaggi'}
          </span>
        </div>
      </div>

      {/* Riga "Collegato a": visibile solo a thread collegato */}
      {linkedClient && (
        <ThreadLinkBar
          client={linkedClient}
          senderEmail={threadSenderEmail}
          organizationId={organizationId}
          onUnlink={handleUnlink}
        />
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {selectedMessages.map((msg, index) => (
          <MessageCard
            key={msg.id}
            message={msg}
            isLast={index === selectedMessages.length - 1}
            onReply={() => handleReply(msg, 'reply')}
            onReplyAll={() => handleReply(msg, 'replyAll')}
            onForward={() => handleReply(msg, 'forward')}
            organizationId={organizationId}
            userId={userId}
            openInfo={openStatus.get(msg.message_id)}
            // Il badge della scheda non ha senso quando quella scheda e` di un collega.
            showClientBadge={!isInternalClient(msg.client, internalEmails)}
          />
        ))}

        {/* Bozza del thread (ultimo elemento, come Gmail) */}
        {draft && (
          <button
            onClick={() => openDraftEditor(draft)}
            className="w-full text-left rounded-lg border border-[#DC2626]/30 bg-[#DC2626]/[0.03] dark:bg-[#DC2626]/10 px-4 py-3 hover:border-[#DC2626]/50 transition-colors"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-[#DC2626]">Bozza</span>
              <span className="text-xs text-[#787774] dark:text-white/40">— continua a scrivere</span>
            </div>
            <p className="text-sm text-[#37352F] dark:text-white/80 line-clamp-2">
              {draft.snippet || draft.subject || '(nessun contenuto)'}
            </p>
          </button>
        )}
      </div>

      {/* Quick reply bar */}
      <div className="px-4 py-3 border-t border-[rgba(55,53,47,0.09)] dark:border-gray-800">
        <button
          onClick={() => {
            const lastMsg = selectedMessages[selectedMessages.length - 1];
            if (lastMsg) handleReply(lastMsg);
          }}
          className="w-full px-4 py-2.5 text-left text-sm text-[#787774] dark:text-white/40 bg-[rgba(55,53,47,0.04)] dark:bg-white/5 rounded-lg hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/8 transition-colors"
        >
          Clicca per rispondere...
        </button>
      </div>

      {/* Task Modal */}
      <MailTaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        messages={selectedMessages}
        threadId={selectedThreadId || ''}
      />

      {/* Link to Entity Drawer (lead/cliente, offerta, progetto) */}
      <MailLinkEntityDrawer
        isOpen={showLinkDrawer}
        onClose={() => setShowLinkDrawer(false)}
        threads={linkThreads}
        organizationId={organizationId}
        userId={userId}
      />
    </div>
  );
}

function MessageCard({
  message,
  isLast,
  onReply,
  onReplyAll,
  onForward,
  organizationId,
  userId,
  openInfo,
  showClientBadge = true,
}: {
  message: MailMessage;
  isLast: boolean;
  onReply: () => void;
  onReplyAll: () => void;
  onForward: () => void;
  organizationId: string;
  userId: string;
  openInfo?: MailOpenInfo;
  showClientBadge?: boolean;
}) {
  const { useStore } = useMailProvider();
  const { downloadAttachment } = useStore();
  const { toast } = useApp();
  const [downloadingIdx, setDownloadingIdx] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(isLast);
  const date = new Date(message.internal_date);
  const dateStr = date.toLocaleString('it-IT', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const senderName = message.from_name || message.from_email;

  return (
    <div className="rounded-lg border border-[rgba(55,53,47,0.09)] dark:border-gray-800 overflow-hidden">
      {/* Message header */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => { if (e.key === 'Enter') setExpanded(!expanded); }}
        className="group w-full flex items-center gap-3 px-4 py-3 hover:bg-[rgba(55,53,47,0.02)] dark:hover:bg-white/2 transition-colors cursor-pointer"
      >
        <div className="w-8 h-8 rounded-full bg-[rgba(55,53,47,0.06)] dark:bg-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
          {showClientBadge && message.client?.logo_url ? (
            <img src={message.client.logo_url} alt="" className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          ) : showClientBadge && message.client ? (
            <span className="text-xs font-medium text-[#37352F] dark:text-white">
              {(message.client.business_name || message.client.contact_name || '?').charAt(0).toUpperCase()}
            </span>
          ) : (
            <User className="h-3.5 w-3.5 text-[#787774]" />
          )}
        </div>

        <div className="flex-1 min-w-0 text-left">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#37352F] dark:text-white truncate">
              {senderName}
            </span>
            {message.client && showClientBadge && (
              <span className="text-[10px] text-[#2383E2] bg-[#2383E2]/10 px-1 rounded">
                {/* Gli adapter selezionano business_name/contact_name (first/last_name
                    non sono nel SELECT: comporrebbero "undefined undefined"). */}
                {message.client.business_name || (message.client as any).contact_name || 'Cliente'}
              </span>
            )}
            {openInfo && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium text-green-600 dark:text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded"
                title={openInfo.lastOpenedAt ? `Aperta ${openInfo.openCount}× · ultima ${new Date(openInfo.lastOpenedAt).toLocaleString('it-IT')}` : 'Aperta'}
              >
                <MailOpen className="h-3 w-3" />
                Aperta{openInfo.openCount > 1 ? ` ${openInfo.openCount}×` : ''}
              </span>
            )}
          </div>
          {!expanded && (
            <p className="text-xs text-[#787774] dark:text-white/40 truncate">
              {message.snippet}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Iconcine rapide (come Gmail): visibili su hover, non serve scrollare */}
          <div className="hidden group-hover:flex items-center gap-0.5">
            <button onClick={(e) => { e.stopPropagation(); onReply(); }} className="p-1.5 rounded-md text-[#787774] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 transition-colors" title="Rispondi"><Reply className="h-3.5 w-3.5" /></button>
            <button onClick={(e) => { e.stopPropagation(); onReplyAll(); }} className="p-1.5 rounded-md text-[#787774] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 transition-colors" title="Rispondi a tutti"><ReplyAll className="h-3.5 w-3.5" /></button>
            <button onClick={(e) => { e.stopPropagation(); onForward(); }} className="p-1.5 rounded-md text-[#787774] hover:text-[#37352F] dark:hover:text-white hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/10 transition-colors" title="Inoltra"><Forward className="h-3.5 w-3.5" /></button>
          </div>
          <span className="text-xs text-[#787774] dark:text-white/40">{dateStr}</span>
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5 text-[#787774]" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-[#787774]" />
          )}
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-[rgba(55,53,47,0.06)] dark:border-gray-800/50">
          {/* To/CC info */}
          <div className="px-4 py-2 text-xs text-[#787774] dark:text-white/40 space-y-0.5">
            <div>
              <span className="font-medium">A: </span>
              {(message.to_emails || []).map(e => e.name || e.email).join(', ')}
            </div>
            {message.cc_emails && message.cc_emails.length > 0 && (
              <div>
                <span className="font-medium">Cc: </span>
                {message.cc_emails.map(e => e.name || e.email).join(', ')}
              </div>
            )}
          </div>

          {/* Body */}
          <div className="px-4 py-3">
            {message.body_html ? (
              <SanitizedHtml html={message.body_html} />
            ) : message.body_text ? (
              <pre className="text-sm text-[#37352F] dark:text-white/80 whitespace-pre-wrap font-sans">
                {message.body_text}
              </pre>
            ) : (
              <p className="text-sm text-[#787774] italic">Caricamento contenuto...</p>
            )}
          </div>

          {/* Attachments */}
          {message.has_attachments && message.attachments_metadata && message.attachments_metadata.length > 0 && (
            <div className="px-4 py-2 border-t border-[rgba(55,53,47,0.06)] dark:border-gray-800/50">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Paperclip className="h-3 w-3 text-[#787774]" />
                <span className="text-xs font-medium text-[#787774]">
                  {message.attachments_metadata.length} allegat{message.attachments_metadata.length === 1 ? 'o' : 'i'}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {message.attachments_metadata.map((att, i) => (
                  <button
                    key={i}
                    onClick={async () => {
                      setDownloadingIdx(i);
                      try {
                        await downloadAttachment(organizationId, userId, message.message_id, att.attachmentId, att.filename, att.mimeType);
                      } catch {
                        toast.error('Errore nel download dell\'allegato');
                      }
                      setDownloadingIdx(null);
                    }}
                    disabled={downloadingIdx === i}
                    className="flex items-center gap-1.5 text-xs px-2 py-1 bg-[rgba(55,53,47,0.04)] dark:bg-white/5 rounded text-[#37352F] dark:text-white/70 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    {downloadingIdx === i ? (
                      <Loader className="h-3 w-3 animate-spin" />
                    ) : (
                      <Download className="h-3 w-3" />
                    )}
                    {att.filename}
                    {att.size > 0 && (
                      <span className="text-[#787774] dark:text-white/40">
                        ({(att.size / 1024).toFixed(0)}KB)
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="px-4 py-2 border-t border-[rgba(55,53,47,0.06)] dark:border-gray-800/50 flex items-center gap-1.5">
            <button
              onClick={onReply}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#37352F] dark:text-white bg-[rgba(55,53,47,0.04)] dark:bg-white/5 rounded-md hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
            >
              <Reply className="h-3.5 w-3.5" />
              Rispondi
            </button>
            <button
              onClick={onReplyAll}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#37352F] dark:text-white bg-[rgba(55,53,47,0.04)] dark:bg-white/5 rounded-md hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
            >
              <ReplyAll className="h-3.5 w-3.5" />
              Rispondi a tutti
            </button>
            <button
              onClick={onForward}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#37352F] dark:text-white bg-[rgba(55,53,47,0.04)] dark:bg-white/5 rounded-md hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors"
            >
              <Forward className="h-3.5 w-3.5" />
              Inoltra
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
